import uuid from "uuid-random";
import {
  AssignationSessionsPreviewResult,
  EditionMode,
  EditorChangesetInput,
  EditorOperation,
} from "@models/ISchema";
import type {
  SessionAssignationChanges,
  SessionAssignationGroup,
  SessionAssignationOperation,
  SessionAssignationOriginal,
  SessionAssignationPolicy,
  SessionAssignationRow,
  SessionAssignationRowValues,
  SessionAssignationSeed,
} from "@services/types/session-assignation/sessionAssignationTypes";
import {
  DAY_BY_ID,
  idSetKey,
  normalizeTime,
  occurrenceKey,
  orderedBlocks,
  SessionAssignationError,
} from "./occurrences";
import { isPastOccurrence } from "../utils/permissions";

type ReconcileContext = Pick<SessionAssignationSeed, "context"> & {
  sections:
    | SessionAssignationSeed["sections"]
    | readonly SessionAssignationSeed["sections"][number][];
  weeks: readonly SessionAssignationSeed["weeks"][number][];
  blocks: readonly SessionAssignationSeed["blocks"][number][];
  originals: readonly SessionAssignationOriginal[];
  originalRows: readonly SessionAssignationRow[];
  rows: readonly SessionAssignationRow[];
  policy: SessionAssignationPolicy;
};

const groupRows = (group: SessionAssignationGroup): SessionAssignationRowValues[] => {
  const days = [...new Set(group.blocks.map(block => block.day))];
  return group.intervalIds.flatMap(intervalId =>
    (days.length ? days : [null]).map(day => {
      const blocks = orderedBlocks(group.blocks.filter(block => block.day === day));
      return {
        sectionId: group.sectionId,
        intervalId,
        date: "",
        scheduleMode: blocks.length ? "scheduled" : "unscheduled",
        dayId: day ? Number(Object.entries(DAY_BY_ID).find(([, name]) => name === day)?.[0]) : null,
        startingTime: blocks.length ? normalizeTime(blocks[0].startingTime) : null,
        endingTime: blocks.length ? normalizeTime(blocks[blocks.length - 1].endingTime) : null,
        blocksIds: blocks.map(block => block.id),
        blocksCountInput: String(group.blocksCount),
        classrooms: group.classrooms,
        instructors: group.instructors,
      };
    }),
  );
};

const groupKeys = (group: SessionAssignationGroup): string[] => groupRows(group).map(occurrenceKey);

const groupPatternKey = (group: SessionAssignationGroup): string =>
  JSON.stringify([
    group.sectionId,
    idSetKey(group.blocks.map(block => block.id)),
    group.blocks.length ? null : group.blocksCount,
    idSetKey(group.classrooms.map(item => item.id)),
    idSetKey(group.instructors.map(item => item.id)),
  ]);

const combineCompatibleDays = (
  groups: readonly SessionAssignationGroup[],
): SessionAssignationGroup[] => {
  const combined = new Map<string, SessionAssignationGroup>();
  for (const group of groups) {
    if (!group.blocks.length) {
      combined.set(`unscheduled:${groupPatternKey(group)}:${idSetKey(group.intervalIds)}`, group);
      continue;
    }
    const blocks = orderedBlocks(group.blocks);
    const key = JSON.stringify([
      group.sectionId,
      blocks[0].startingTime,
      blocks[blocks.length - 1].endingTime,
      idSetKey(group.intervalIds),
      idSetKey(group.classrooms.map(item => item.id)),
      idSetKey(group.instructors.map(item => item.id)),
    ]);
    const previous = combined.get(key);
    combined.set(
      key,
      previous
        ? {
            ...previous,
            blocks: [...previous.blocks, ...group.blocks],
            originSessionIds: [
              ...new Set([...previous.originSessionIds, ...group.originSessionIds]),
            ],
          }
        : group,
    );
  }
  return [...combined.values()];
};

const previewGroups = (preview: AssignationSessionsPreviewResult): SessionAssignationGroup[] =>
  preview.sections.flatMap(section =>
    section.sessions.flatMap(session => {
      if (!session.assignment || !session.assignment.intervals.length) {
        throw new SessionAssignationError("INCOMPLETE_PREVIEW");
      }
      const blocks = session.assignment.blocks;
      if (!blocks.length && (!Number.isInteger(session.blocksCount) || session.blocksCount < 1)) {
        throw new SessionAssignationError("INCOMPLETE_PREVIEW");
      }
      const days = [...new Set(blocks.map(block => block.day))];
      return (days.length ? days : [null]).map(day => ({
        sectionId: section.id,
        intervalIds: session.assignment.intervals.map(interval => interval.id),
        blocks: orderedBlocks(blocks.filter(block => block.day === day)),
        blocksCount: session.blocksCount,
        classrooms: session.classrooms,
        instructors: session.instructors,
        originSessionIds: session.originSessionIds,
      }));
    }),
  );

const groupFromOriginalRows = (
  original: SessionAssignationOriginal,
  rows: readonly SessionAssignationRow[],
  catalog: ReconcileContext["blocks"],
): SessionAssignationGroup => {
  const first = rows[0];
  const blockIds = [...new Set(rows.flatMap(row => [...row.blocksIds]))];
  const blocks = catalog.filter(block => blockIds.includes(block.id));
  if (blocks.length !== blockIds.length)
    throw new SessionAssignationError("INCOMPLETE_BLOCK_CATALOG");
  if (
    rows.some(
      row =>
        row.scheduleMode !== first.scheduleMode ||
        idSetKey(row.classrooms.map(item => item.id)) !==
          idSetKey(first.classrooms.map(item => item.id)) ||
        idSetKey(row.instructors.map(item => item.id)) !==
          idSetKey(first.instructors.map(item => item.id)) ||
        (row.scheduleMode === "unscheduled" && row.blocksCountInput !== first.blocksCountInput),
    )
  ) {
    throw new SessionAssignationError("UNREPRESENTABLE_ASSIGNMENT");
  }
  return {
    sectionId: original.sectionId,
    intervalIds: [...new Set(rows.map(row => row.intervalId))],
    blocks,
    blocksCount: Number(first.blocksCountInput),
    classrooms: first.classrooms,
    instructors: first.instructors,
    originSessionIds: [original.session.id],
  };
};

const toChangeset = (
  group: SessionAssignationGroup,
  sessionId: string,
  catalog: ReconcileContext["blocks"],
): EditorChangesetInput => {
  const days = Object.values(DAY_BY_ID).filter(day =>
    group.blocks.some(block => block.day === day),
  );
  let schedule: Pick<EditorChangesetInput, "changeBlocks" | "noScheduleEventsCount">;
  if (!days.length) {
    schedule = { noScheduleEventsCount: group.blocksCount };
  } else {
    const ranges = days.map(day => {
      const blocks = orderedBlocks(group.blocks.filter(block => block.day === day));
      const startTime = normalizeTime(blocks[0].startingTime);
      const endTime = normalizeTime(blocks[blocks.length - 1].endingTime);
      const represented = catalog.filter(
        block =>
          block.day === day &&
          normalizeTime(block.startingTime) >= startTime &&
          normalizeTime(block.endingTime) <= endTime,
      );
      if (
        idSetKey(represented.map(block => block.id)) !== idSetKey(blocks.map(block => block.id))
      ) {
        throw new SessionAssignationError("UNREPRESENTABLE_ASSIGNMENT");
      }
      return { startTime, endTime };
    });
    if (
      ranges.some(
        range => range.startTime !== ranges[0].startTime || range.endTime !== ranges[0].endTime,
      )
    ) {
      throw new SessionAssignationError("UNREPRESENTABLE_ASSIGNMENT");
    }
    schedule = {
      changeBlocks: {
        op: EditorOperation.Change,
        ...ranges[0],
        ...(days.length === 1 ? { day: days[0] } : { days }),
      },
    };
  }
  return {
    sessionId,
    sectionId: group.sectionId,
    ...schedule,
    changeIntervals: {
      op: EditorOperation.Change,
      uniqueInterval: false,
      intervalIds: [...group.intervalIds],
    },
    changeClassroom: {
      op: EditorOperation.Change,
      classroomIds: group.classrooms.map(item => item.id),
    },
    changeInstructor: {
      op: EditorOperation.Change,
      instructorIds: group.instructors.map(item => item.id),
    },
  };
};

/** Converts a complete, successful preview to assignment operations without deleting sections. */
export const reconcileAssignationPreview = (
  preview: AssignationSessionsPreviewResult,
  state: ReconcileContext,
  createId: () => string = uuid,
): SessionAssignationChanges => {
  if (preview.validationErrors.length) throw new SessionAssignationError("PREVIEW_HAS_ERRORS");
  const sectionIds = state.sections.map(section => section.id);
  const previewSectionIds = preview.sections.map(section => section.id);
  if (
    new Set(previewSectionIds).size !== previewSectionIds.length ||
    idSetKey(sectionIds) !== idSetKey(previewSectionIds)
  ) {
    throw new SessionAssignationError("INCOMPLETE_PREVIEW");
  }
  const retained = state.rows.filter(row => row.pendingState !== "deleted");
  const desiredKeys = new Set(retained.map(occurrenceKey));
  const groups = previewGroups(preview);
  const outputKeys = new Set(groups.flatMap(groupKeys));
  if (idSetKey([...desiredKeys]) !== idSetKey([...outputKeys])) {
    throw new SessionAssignationError("PREVIEW_DOES_NOT_MATCH_DRAFT");
  }

  const consumed = new Set<string>();
  const usedOriginals = new Set<string>();
  const updates: EditorChangesetInput[] = [];
  const creates: EditorChangesetInput[] = [];
  const deletes: string[] = [];
  const operations: SessionAssignationOperation[] = [];
  const rowsByOriginal = new Map(
    state.originals.map(original => [
      original.session.id,
      state.originalRows.filter(row => row.origin?.sessionId === original.session.id),
    ]),
  );
  const protectedRows = (original: SessionAssignationOriginal) =>
    (rowsByOriginal.get(original.session.id) ?? []).filter(row => {
      const week = state.weeks.find(item => item.id === row.intervalId);
      return (
        !week || week.isInTerm === false || isPastOccurrence(row, state.policy, week.startingDate)
      );
    });
  const originals = [...state.originals].sort(
    (a, b) =>
      Number(protectedRows(b).length > 0) - Number(protectedRows(a).length > 0) ||
      a.session.id.localeCompare(b.session.id, undefined, { numeric: true }),
  );
  const rowIdsForGroup = (group: SessionAssignationGroup): string[] => {
    const keys = new Set(groupKeys(group));
    return retained.filter(row => keys.has(occurrenceKey(row))).map(row => row.localId);
  };
  const recordUpdate = (original: SessionAssignationOriginal, group: SessionAssignationGroup) => {
    updates.push(toChangeset(group, original.session.id, state.blocks));
    operations.push({
      kind: "update",
      correlationId: original.session.id,
      sectionId: original.sectionId,
      originalSessionId: original.session.id,
      rowIds: rowIdsForGroup(group),
      assignment: group,
    });
    usedOriginals.add(original.session.id);
    groupKeys(group).forEach(key => consumed.add(key));
  };

  // Reserve complete unchanged originals first, including multi-day originals and sessions without occurrences.
  for (const original of originals) {
    const rows = rowsByOriginal.get(original.session.id) ?? [];
    const keys = rows.map(occurrenceKey);
    if (!rows.length || keys.every(key => desiredKeys.has(key) && !consumed.has(key))) {
      usedOriginals.add(original.session.id);
      keys.forEach(key => consumed.add(key));
    }
  }

  // Keep a protected original's unchanged weeks together; create changed future groups separately.
  for (const original of originals) {
    if (usedOriginals.has(original.session.id)) continue;
    const locked = protectedRows(original);
    if (!locked.length) continue;
    const rows = rowsByOriginal.get(original.session.id) ?? [];
    const keepIntervals = [...new Set(rows.map(row => row.intervalId))].filter(intervalId =>
      rows
        .filter(row => row.intervalId === intervalId)
        .every(row => desiredKeys.has(occurrenceKey(row)) && !consumed.has(occurrenceKey(row))),
    );
    const baseRows = rows.filter(row => keepIntervals.includes(row.intervalId));
    const baseKeys = new Set(baseRows.map(occurrenceKey));
    if (locked.every(row => baseKeys.has(occurrenceKey(row)))) {
      recordUpdate(original, groupFromOriginalRows(original, baseRows, state.blocks));
      continue;
    }
    const candidate = combineCompatibleDays(groups).find(group => {
      const keys = new Set(groupKeys(group));
      return (
        group.sectionId === original.sectionId &&
        locked.every(row => keys.has(occurrenceKey(row))) &&
        [...keys].every(key => !consumed.has(key))
      );
    });
    if (!candidate) throw new SessionAssignationError("PROTECTED_OCCURRENCES_CANNOT_BE_RECONCILED");
    recordUpdate(original, candidate);
  }

  const remaining = groups
    .map(group => ({
      ...group,
      intervalIds: group.intervalIds.filter(intervalId =>
        groupKeys({ ...group, intervalIds: [intervalId] }).every(key => !consumed.has(key)),
      ),
    }))
    .filter(group => group.intervalIds.length);
  const combined = combineCompatibleDays(remaining);
  const overlap = (original: SessionAssignationOriginal, group: SessionAssignationGroup) => {
    const keys = new Set(groupKeys(group));
    return (rowsByOriginal.get(original.session.id) ?? []).filter(row =>
      keys.has(occurrenceKey(row)),
    ).length;
  };
  // Prefer groups that retain original occurrences before groups that change their schedule/resources.
  combined.sort(
    (a, b) =>
      Math.max(
        0,
        ...originals
          .filter(original => !usedOriginals.has(original.session.id))
          .map(original => overlap(original, b)),
      ) -
        Math.max(
          0,
          ...originals
            .filter(original => !usedOriginals.has(original.session.id))
            .map(original => overlap(original, a)),
        ) || groupPatternKey(a).localeCompare(groupPatternKey(b)),
  );
  for (const group of combined) {
    const candidates = originals.filter(
      original =>
        !usedOriginals.has(original.session.id) &&
        original.sectionId === group.sectionId &&
        (group.originSessionIds.includes(original.session.id) || overlap(original, group) > 0),
    );
    candidates.sort(
      (a, b) =>
        overlap(b, group) - overlap(a, group) ||
        a.session.id.localeCompare(b.session.id, undefined, { numeric: true }),
    );
    if (candidates.length) {
      recordUpdate(candidates[0], group);
    } else {
      const correlationId = createId();
      creates.push(toChangeset(group, correlationId, state.blocks));
      operations.push({
        kind: "create",
        correlationId,
        sectionId: group.sectionId,
        originalSessionId: null,
        rowIds: rowIdsForGroup(group),
        assignment: group,
      });
    }
  }
  for (const original of originals) {
    if (usedOriginals.has(original.session.id)) continue;
    if (protectedRows(original).length)
      throw new SessionAssignationError("PROTECTED_OCCURRENCES_CANNOT_BE_RECONCILED");
    deletes.push(original.session.id);
    operations.push({
      kind: "delete",
      correlationId: original.session.id,
      sectionId: original.sectionId,
      originalSessionId: original.session.id,
      rowIds: state.rows
        .filter(row => row.origin?.sessionId === original.session.id)
        .map(row => row.localId),
      assignment: null,
    });
  }
  return {
    input: {
      linkId: state.context.linkId,
      editionMode: EditionMode.Assignation,
      dryRun: true,
      skipValidations: false,
      changesets: { updates, creates, deletes: { sections: [], sessions: deletes } },
    },
    operations,
  };
};
